import { useEffect } from 'react';
import { useForecastMap, mapAlive } from './ForecastMapContext';
import { getDecodedTile } from './wind/tileCache';

// Hypsometric colour relief for the Terrain basemap (Skysight-style): decodes
// terrarium DEM tiles (elev = R*256 + G + B/256 − 32768) and recolours them by
// elevation; the style's hillshade layer shades it from above. Rendered as a
// viewport image source like the wind fills.
const SOURCE_ID = 'terrain-relief';
const LAYER_ID = 'terrain-relief:raster';
const TILE = 256;

// elevation (m) → [r,g,b], approximating Skysight's relief ramp.
const STOPS = [
  [0, [30, 80, 200]],
  [150, [60, 140, 235]],
  [300, [90, 196, 137]],
  [600, [70, 170, 60]],
  [900, [160, 205, 70]],
  [1200, [235, 230, 80]],
  [1600, [240, 180, 60]],
  [2000, [235, 120, 45]],
  [2600, [210, 60, 35]],
  [3200, [160, 60, 60]],
  [4000, [245, 245, 245]],
];

const colorForElev = (e) => {
  if (e <= STOPS[0][0]) return STOPS[0][1];
  for (let i = 1; i < STOPS.length; i++) {
    if (e <= STOPS[i][0]) {
      const [e0, c0] = STOPS[i - 1];
      const [e1, c1] = STOPS[i];
      const f = (e - e0) / (e1 - e0);
      return [0, 1, 2].map((k) => Math.round(c0[k] + (c1[k] - c0[k]) * f));
    }
  }
  return STOPS[STOPS.length - 1][1];
};

const lngLatToTile = (lng, lat, z) => {
  const n = Math.pow(2, z);
  const x = Math.floor(((lng + 180) / 360) * n);
  const y = Math.floor(((1 - Math.asinh(Math.tan((lat * Math.PI) / 180)) / Math.PI) / 2) * n);
  return [Math.max(0, Math.min(n - 1, x)), Math.max(0, Math.min(n - 1, y))];
};
const tileLng = (x, n) => (x / n) * 360 - 180;
const tileLat = (y, n) => (Math.atan(Math.sinh(Math.PI * (1 - (2 * y) / n))) * 180) / Math.PI;

const TerrainReliefLayer = () => {
  const map = useForecastMap();

  useEffect(() => {
    if (!map) return undefined;
    let cancelled = false;
    const controller = new AbortController();

    const refresh = async () => {
      if (cancelled) return;
      const z = Math.max(4, Math.min(11, Math.round(map.getZoom())));
      const n = Math.pow(2, z);
      const b = map.getBounds();
      const [x0, y0] = lngLatToTile(b.getWest(), b.getNorth(), z);
      const [x1, y1] = lngLatToTile(b.getEast(), b.getSouth(), z);
      const xMin = Math.min(x0, x1), xMax = Math.max(x0, x1);
      const yMin = Math.min(y0, y1), yMax = Math.max(y0, y1);
      const cols = xMax - xMin + 1, rows = yMax - yMin + 1;
      if (cols * rows > 24) return;
      const canvas = document.createElement('canvas');
      canvas.width = cols * TILE; canvas.height = rows * TILE;
      const ctx = canvas.getContext('2d');
      let any = false;
      await Promise.all(
        Array.from({ length: cols * rows }, (_, k) => {
          const x = xMin + (k % cols), y = yMin + Math.floor(k / cols);
          const url = `https://s3.amazonaws.com/elevation-tiles-prod/terrarium/${z}/${x}/${y}.png`;
          return getDecodedTile(url, controller.signal)
            .then((tile) => {
              if (cancelled || !tile) return;
              const img = new ImageData(TILE, TILE);
              for (let i = 0; i < TILE * TILE; i++) {
                const si = i * 4;
                const elev = tile.data[si] * 256 + tile.data[si + 1] + tile.data[si + 2] / 256 - 32768;
                if (elev <= 0) { img.data[si + 3] = 0; continue; } // sea → transparent (bg shows)
                const [r, g, bl] = colorForElev(elev);
                img.data[si] = r; img.data[si + 1] = g; img.data[si + 2] = bl; img.data[si + 3] = 255;
              }
              ctx.putImageData(img, (x - xMin) * TILE, (y - yMin) * TILE);
              any = true;
            })
            .catch(() => {});
        }),
      );
      if (cancelled || !any) return;
      const coordinates = [
        [tileLng(xMin, n), tileLat(yMin, n)],
        [tileLng(xMax + 1, n), tileLat(yMin, n)],
        [tileLng(xMax + 1, n), tileLat(yMax + 1, n)],
        [tileLng(xMin, n), tileLat(yMax + 1, n)],
      ];
      const url = canvas.toDataURL('image/png');
      const existing = map.getSource(SOURCE_ID);
      if (existing) {
        existing.updateImage({ url, coordinates });
      } else {
        map.addSource(SOURCE_ID, { type: 'image', url, coordinates });
        // under the hillshade so shading multiplies over the colours
        map.addLayer({
          id: LAYER_ID, type: 'raster', source: SOURCE_ID,
          paint: { 'raster-opacity': 1, 'raster-resampling': 'linear', 'raster-fade-duration': 0 },
        }, map.getLayer('basemap-relief') ? 'basemap-relief' : undefined);
      }
    };

    refresh();
    map.once('idle', refresh);
    map.on('moveend', refresh);
    return () => {
      cancelled = true;
      controller.abort();
      map.off('idle', refresh);
      map.off('moveend', refresh);
      if (!mapAlive(map)) return;
      if (map.getLayer(LAYER_ID)) map.removeLayer(LAYER_ID);
      if (map.getSource(SOURCE_ID)) map.removeSource(SOURCE_ID);
    };
  }, [map]);

  return null;
};

export default TerrainReliefLayer;
